<script setup lang="ts">
import {computed, ref} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupCollectionClosingCard from "@/medication-checkup/components/MedicationCheckupCollectionClosingCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {CollectionClosingAnswers, CollectionClosingView} from "@/medication-checkup/interfaces/collection-closing";
import {ACTION_DRAFT} from "@/medication-checkup/constants";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useKnowledgeAppraisal} from "@/medication-checkup/composables/useKnowledgeAppraisal";
import {medicationCheckupApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import fileHelper from "@/core/helpers/fileHelper";
import {closingDocumentFilename} from "@/medication-checkup/composables/useClosingDocument";

/**
 * The scene closing the collection interview: the end of the first step.
 *
 * Everything it shows is served by the handler: the words, the two fields
 * with their labels, the conclusion the AI drafted, the billing reminder and
 * — once validated — the texts recorded, the appraisal of what the patient
 * knows and the recap to download. Planning the next appointment is the
 * scene that follows, and a server serving a closing line still has it
 * shown. Typing is the scene's own state: the closing action, always
 * available since nothing is required, submits it all at once.
 *
 * Landing on the live block asks the server for the draft, the way the
 * collection asks for its scan: the proposal comes back in the served view,
 * and the pharmacist's typing takes over it from there.
 */

const props = defineProps<SceneProps<CollectionClosingView>>();

/**
 * The draft is asked for as the scene opens, so its wait overlaps the
 * typing of the bubble.
 */
const {bubbleShown, cardShown, clicked, view, text, actions, closing, acted, act: submit} = useScene(props, {
    card: true,
    live: () => {
        if (view.value?.conclusion === null && view.value?.conclusion_provenance === null) {
            draft();
        }
    }
});

const store = useMedicationCheckupStore();

const downloading = ref(false);

/**
 * Whether the draft of the conclusion is being asked for.
 */
const drafting = ref(false);

/**
 * The texts typed so far — local until the closing action submits them. The
 * conclusion stays null until the pharmacist types into it, so the proposal
 * served in the meantime shows through.
 */
const answers = ref<CollectionClosingAnswers>({questions: '', conclusion: null});

const {appraisal, submitted} = useKnowledgeAppraisal();

/**
 * The conclusion as it stands: what the pharmacist typed, else the proposal
 * the server serves, else nothing.
 */
const conclusion = computed(() => answers.value.conclusion ?? view.value?.conclusion ?? '');

/**
 * The texts the card shows while live, the conclusion resolved.
 */
const effective = computed<CollectionClosingAnswers>(() => ({
    questions: answers.value.questions,
    conclusion: conclusion.value
}));

/**
 * Ask the server for the draft of the conclusion.
 *
 * The answered cycle carries the proposal in the served view — nothing to
 * seed locally. A failed ask leaves the field empty and the closing goes on
 * by hand: the store has already reported why.
 */
const draft = async () => {
    drafting.value = true;

    try {
        await store.submitBlock(props.block.id, {action: ACTION_DRAFT});
    } catch {
        // Reported by the store; the pharmacist concludes without a proposal.
    } finally {
        drafting.value = false;
    }
};

/**
 * Type into one field — local only: the closing action submits both.
 */
const typed = (field: keyof CollectionClosingAnswers, value: string) => {
    answers.value = {...answers.value, [field]: value};
};

/**
 * Take the closing action, submitting the two texts and the appraisal.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    questions: answers.value.questions.trim() || null,
    conclusion: conclusion.value.trim() || null,
    ...submitted()
});

/**
 * Download the recap the closing serves, saved under a dated name: the HDS
 * renders it on request, so the wait is the generation itself.
 */
const download = async () => {
    const recap = view.value?.collection_recap;

    if (!recap || downloading.value) {
        return;
    }

    downloading.value = true;

    try {
        const blob = await medicationCheckupApi().downloadDocument(recap.url);
        fileHelper.downloadBlob(closingDocumentFilename(recap, 'recapitulatif-recueil'), blob);
    } catch {
        useToastStore().showError({content: "Le récapitulatif n'a pas pu être téléchargé."});
    } finally {
        downloading.value = false;
    }
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupCollectionClosingCard
        v-if="view"
        :view="view"
        :downloading="downloading"
        frozen
        @download="download"
      />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupCollectionClosingCard
        v-if="cardShown && view"
        :view="view"
        :answers="effective"
        :appraisal="appraisal"
        :drafting="drafting"
        class="animate-message-in"
        @typed="typed"
        @appraised="appraisal = $event"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :disabled="drafting"
        @act="act"
      />
    </template>
  </div>
</template>
